// Shadow Network — ProGate component.
// Phase 2 — additive. Wraps any UI surface and shows a paywall overlay when
// the current user can't access it. The children are still rendered behind a
// blur so the user can see WHAT they're missing (drives upgrade intent).
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { canUse, isPro, PERMISOS } from "../../lib/shadow-permissions";
import { API_BASE as API } from "../../lib/api";

const I18N = {
  titulo: {
    es: "Función exclusiva PRO",
    en: "PRO exclusive feature",
    sk: "Exkluzívna PRO funkcia", de: "Exklusive PRO-Funktion", "zh-CN": "PRO 专属功能",
  },
  sub: {
    es: "Actualiza a PRO para desbloquear esta función",
    en: "Upgrade to PRO to unlock this feature",
    sk: "Prejdi na PRO a odomkni túto funkciu", de: "Upgrade auf PRO, um diese Funktion freizuschalten", "zh-CN": "升级至 PRO 解锁此功能",
  },
  cta: {
    es: "Hacerse PRO →",
    en: "Go PRO →",
    sk: "Stať sa PRO →", de: "PRO werden →", "zh-CN": "升级至 PRO →",
  },
  login: {
    es: "Inicia sesión",
    en: "Sign in",
    sk: "Prihlásiť sa", de: "Anmelden", "zh-CN": "登录",
  },
  loginPrompt: {
    es: "Inicia sesión para acceder a esta sección.",
    en: "Sign in to access this section.",
    sk: "Prihlás sa pre prístup k tejto sekcii.", de: "Melden Sie sich an, um auf diesen Bereich zuzugreifen.", "zh-CN": "登录以访问此部分。",
  },
  errCheckout: {
    es: "No se pudo iniciar el pago. Reinténtalo.",
    en: "Couldn't start checkout. Please retry.",
    sk: "Platba sa nedala spustiť. Skús znova.", de: "Die Zahlung konnte nicht gestartet werden. Bitte versuche es erneut.", "zh-CN": "无法启动支付。请重试。",
  },
};

// Shared Stripe checkout starter. Mirrors the call SimulatorPage already makes
// successfully in production. Resolves the redirect URL and sends the browser
// there. Returns an error string on failure (caller can show it).
//
// Exported so other gated CTAs (e.g. PublishAnalysisModal) reuse the exact
// same flow instead of redirecting to /simulator or anywhere else.
export const startProCheckout = async () => {
  const token = localStorage.getItem("rebel_jwt");
  const res = await fetch(`${API}/api/payments/checkout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      plan: "pro_lifetime",
      origin_url: window.location.origin,
    }),
  });
  // Clone before reading — defends against fetch interceptors swallowing body.
  const data = await res.clone().json().catch(() => ({}));
  if (!res.ok || !data?.url) {
    throw new Error(data?.detail || data?.error || `checkout_failed_${res.status}`);
  }
  window.location.href = data.url;
};

const ProGate = ({
  feature,
  children,
  className = "",
  blur = true,
  compact = false,
}) => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Free feature OR user has access → pass through (no overlay).
  if (PERMISOS[feature] === "free" && user) return children;
  if (canUse(feature, user)) return children;

  const handleUpgrade = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      await startProCheckout();
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error("[ProGate] checkout failed:", e2);
      setErr(I18N.errCheckout[lang]);
      setBusy(false);
    }
  };

  // Not signed in → "please log in" CTA.
  if (!user) {
    return (
      <div
        className={`relative overflow-hidden rounded-2xl border border-white/10 bg-black/40 p-6 text-center ${className}`}
        data-testid={`progate-${feature}`}
      >
        <Lock className="mx-auto mb-3 h-6 w-6 text-white/40" />
        <p className="text-sm text-white/65 mb-4">{I18N.loginPrompt[lang]}</p>
        <Link
          to="/login"
          className="inline-flex items-center justify-center rounded-full bg-white text-black px-5 py-2 text-xs font-bold uppercase tracking-[0.18em] hover:bg-amber-300 transition-colors"
        >
          {I18N.login[lang]}
        </Link>
      </div>
    );
  }

  // Authenticated but not PRO. We render a SELF-CONTAINED card with our own
  // padding/min-height instead of overlay-ing children with `absolute inset-0`.
  // The previous absolute-overlay approach broke the layout when the wrapped
  // children had pointer-events:none (height collapsed under certain flex
  // contexts), making the overlay overflow into the next section. This card
  // always reserves its own height and never spills.
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-amber-400/25 ${className}`}
      data-testid={`progate-${feature}`}
      style={{
        background: "rgba(7,8,15,0.92)",
        // Reserve enough vertical space so the gate doesn't look squashed,
        // but never let it stretch beyond the natural section size.
        minHeight: compact ? 140 : 200,
      }}
    >
      {/* Optional decorative blurred preview of what's underneath. We keep the
          children rendered (visually muted) so the operator sees the section
          they're missing — but we hide them with display:none on compact
          mode to keep the gate tight and avoid any overflow surprises. */}
      {!compact && (
        <div
          aria-hidden="true"
          className="pointer-events-none select-none"
          style={{
            filter: blur ? "blur(8px)" : "none",
            opacity: 0.18,
            position: "absolute",
            inset: 0,
            overflow: "hidden",
          }}
        >
          {children}
        </div>
      )}

      <div
        className={`relative z-10 flex flex-col items-center justify-center gap-3 text-center ${
          compact ? "px-4 py-5" : "px-4 py-10"
        }`}
      >
        <div
          className="inline-flex h-10 w-10 items-center justify-center rounded-full"
          style={{ background: "rgba(245,166,35,0.18)" }}
        >
          <Lock className="h-5 w-5 text-amber-400" />
        </div>
        <h3
          className="text-sm font-black uppercase tracking-[0.18em] text-amber-300"
          data-testid={`progate-${feature}-title`}
        >
          ⚡ {I18N.titulo[lang]}
        </h3>
        <p className="text-xs text-white/55 max-w-xs">{I18N.sub[lang]}</p>
        <button
          type="button"
          onClick={handleUpgrade}
          disabled={busy}
          data-testid={`progate-${feature}-cta`}
          className="inline-flex items-center justify-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-2 text-xs font-black uppercase tracking-[0.18em] text-black transition-all hover:from-amber-300 hover:to-amber-400 disabled:opacity-60"
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {I18N.cta[lang]}
        </button>
        {err && (
          <p className="text-[11px] text-rose-300 mt-1" data-testid={`progate-${feature}-error`}>
            {err}
          </p>
        )}
      </div>
    </div>
  );
};

// Inline gate variant — for small CTAs / buttons rather than panels.
export const ProInlineGate = ({ feature, children, fallback }) => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [busy, setBusy] = useState(false);
  if (canUse(feature, user)) return children;
  if (fallback) return fallback;
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.preventDefault();
        if (!user) {
          window.location.href = "/login";
          return;
        }
        if (busy) return;
        setBusy(true);
        try {
          await startProCheckout();
        } catch {
          setBusy(false);
        }
      }}
      disabled={busy}
      data-testid={`progate-inline-${feature}`}
      className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/[0.08] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300 hover:bg-amber-400/[0.18] transition-colors disabled:opacity-60"
      title={I18N.sub[lang]}
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3" />}
      PRO
    </button>
  );
};

export const ProBadge = () => (
  <span
    data-testid="pro-badge"
    className="inline-flex items-center rounded-full bg-amber-400/[0.18] px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.18em] text-amber-300 border border-amber-400/30"
  >
    PRO
  </span>
);

export { isPro };
export default ProGate;
